import React from 'react'
import { user } from 'ampersand-app'
import ThemeInterface from '@/theme-interface'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import { closeDocumentDrawer } from '@/redux/ducks/document/drawer'
import PanelHeader from '@/components/layouts/panel/panel-header'
import { documentDrawerNavigate, saveDocument } from '@/redux/ducks/document/documentDrawerHelpers'
import IconArrowLeft from '@assets/images/icons/arrow_left.svg'
import IconArrowRight from '@assets/images/icons/arrow_right.svg'
import IconClose from '@assets/images/icons/close2.svg'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import Loader from '@/modules/base/components/loader/loader'
import { useUpdateSearchParams } from '@/modules/_helpers/updateSearchParams'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  detailsHeader: {
    backgroundColor: theme.backgroundColor,
    height: 86,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingRight: 15,
  },
  header: {
    order: -1,
    paddingLeft: 30,
    color: theme.black,
    whiteSpace: 'nowrap',
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
  },
  label: {
    fontSize: 20,
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    height: CssHelper.px(28),
    marginLeft: 'auto',
    gap: 5,
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row nowrap',
    gap: 5,
    maxWidth: 250,
    margin: '0 auto',
    fontSize: theme.fontSizes.small,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    outline: 'none',
    cursor: 'pointer',
    transition: 'fill 0.3s ease',
    '&:disabled': {
      cursor: 'unset',
      opacity: 0.5,
      '&:hover': {
        '& svg': {
          fill: theme.disabledColor,
        },
      },
    },
    '&:hover': {
      '& svg': {
        fill: theme.primaryLight,
      },
    },
  },
  tooltip: {
    fontSize: theme.fontSizes.tooltip,
  },
  page: {
    position: 'relative',
  },
  loader: {
    height: '100%',
    opacity: 0.7,
    '& > *': {
      height: 20,
    },
  },
}))

const DocumentDrawerHeader = (props) => {
  const classes = useStyles(props)
  const dispatch = useAppDispatch()

  const {
    analysisDone,
    page,
    loadingPage,
  } = useAppSelector((state) => state.document.drawer)

  const [, updateSearchParams] = useUpdateSearchParams()

  // FLAG:useDocuAi — the values in this panel come from DocuAI's KI-Analyse, so calling the result
  // "OCR" is wrong there (OCR is at most its preprocessing step). Flag-OFF (gini/natif) keeps the
  // established OCR wording verbatim.
  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const analysisResultTitle = useDocuAiEnabled
    ? lang.documents.aiDetailsTitle
    : lang.documents.ocrDetailsTitle

  const isFirstDocument = page?.offset === 1
  const isLastDocument = page?.offset === page?.count

  const onNavigate = (step: number) => {
    saveDocument(() => {
      documentDrawerNavigate(step)
    }, true)
  }

  const onClose = () => {
    saveDocument(() => {
      updateSearchParams([{ key: 'documentId' }, { key: 'rowIndex' }])
      dispatch(closeDocumentDrawer())
    }, true)
  }

  return (
    <PanelHeader className={classes.detailsHeader}>
      <div className={classes.header}>
        <span className={classes.label}>{analysisDone ? analysisResultTitle : lang.documents.detailsTitle}</span>
        {page?.count > 1
          ? <div className={classes.page}>
            {loadingPage && <Loader customClass={classes.loader} width='20px' height='20px' />}
            <span className={classes.navigation}>
              <div className={classes.pager}>
                <Tooltip classes={{ tooltip: classes.tooltip }} arrow title={`${lang.documents.previousDocument} ${isFirstDocument || !page?.previous ? '' : ` - ${page?.previous.decodedName}`}`}>
                  <div>
                    <button
                      className={classes.button}
                      onClick={() => onNavigate(-1)}
                      disabled={isFirstDocument}
                    >
                      <SVGIcon svg={IconArrowLeft} height={'12px'} width={'12px'} />
                    </button>
                  </div>
                </Tooltip>
                <span>{lang.documents.drawer.pager(page?.count, page?.offset)}</span>
                <Tooltip classes={{ tooltip: classes.tooltip }} arrow title={`${lang.documents.nextDocument} ${isLastDocument || !page?.next ? '' : ` - ${page?.next.decodedName}`}`}>
                  <div>
                    <button
                      className={classes.button}
                      onClick={() => onNavigate(1)}
                      disabled={isLastDocument}
                    >
                      <SVGIcon svg={IconArrowRight} height={'12px'} width={'12px'} />
                    </button>
                  </div>
                </Tooltip>
              </div>

            </span>
          </div>
          : null}
      </div>
      <button className={classes.button} onClick={onClose}>
        <SVGIcon svg={IconClose} height={'12px'} width={'12px'} fill={'#000'} />
      </button>
    </PanelHeader>
  )
}

export default DocumentDrawerHeader
